// Jotai atom holding the signed-in user's id.
//
// Why this exists separately from `useAuth`: services outside React
// (background pollers, the future mutation registry, MCP tool
// handlers) need to know who's signed in without pulling React into
// their dependency graph. `jotaiStore.get(atomViewerUserId)` gives
// them a synchronous read.
//
// The atom is *written to* from `AuthProvider` via `setViewerUserId`
// when the session state transitions — React is still the source of
// truth for the session (that's what supabase-js gives us). This
// module publishes that truth through the atom store.
//
// ADR: docs/adr/0022-jotai-atom-store.md

import { atom } from 'jotai'
import { jotaiStore } from '../jotaiStore'

/**
 * Supabase auth.users.id of the currently signed-in user, or null
 * when signed out. Mirrors `session?.user?.id` from AuthProvider.
 *
 * Read-only from consumers. Only AuthProvider writes via the
 * setter below (or through `jotaiStore.set(atomViewerUserId, ...)`
 * in tests).
 */
export const atomViewerUserId = atom<string | null>(null)

/**
 * Called from `AuthProvider` on every auth state transition. Keeps
 * the atom in sync with the Supabase session. Non-React callers
 * reading `jotaiStore.get(atomViewerUserId)` see the latest.
 */
export function setViewerUserId(userId: string | null): void {
  jotaiStore.set(atomViewerUserId, userId)
}

/** Convenience — true iff signed in. Derived atom so consumers
 * reading this don't re-evaluate the null-check themselves. */
export const atomIsSignedIn = atom((get) => get(atomViewerUserId) !== null)
